import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { IoIosNotificationsOutline } from "react-icons/io";
import { format } from "date-fns";
import ApiClient from "../../methods/api/apiClient";
import environment from "../../environment";
import { toast } from "react-toastify";

const SOCKET_URL = environment.api || "http://localhost:5000";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return format(new Date(dateStr), "MMM dd, yyyy");
}

function getRedirectPath(notif) {
  const meta = notif?.meta;
  if (!notif?.sourceId) return null;
  const id = notif.sourceId;
  const date = Date.now();
  const typeToPath = {
    "create-request-quote": `/generate/quote/${id}?date=${date}`,
    "negotiate-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "reject-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "estimate-accept": `/generate/invoice/${id}?date=${date}`,
    "estimate-reject": `/estimate/detail/${id}?date=${date}`,
    invoice: `/invoice/detail/${id}?date=${date}`,
    "shipment-update": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-date-update": `/order/tracking/detail/${id}?date=${date}`,
    "tracking-url": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-with-due": `/order/tracking/detail/${id}?date=${date}`,
    "order-delivered": `/order/tracking/detail/${id}?date=${date}`,
    "payment-completed": `/invoice/detail/${id}?date=${date}`,
  };
  return typeToPath[meta.type] ?? null;
}

function NotificationModal({ notif, onClose, onMarkRead }) {
  if (!notif) return null;

  const handleModalClick = (e) => e.stopPropagation();

  const handleMarkRead = () => {
    if (!notif.isRead) onMarkRead(notif._id);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">
            Notification Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-600 transition-colors"
          />
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </label>
            <p className="mt-1 text-gray-900 font-medium">
              {notif.title || "Notification"}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Message
            </label>
            <p className="mt-1 text-gray-700 leading-relaxed">
              {notif.message}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sent By
            </label>
            <div className="mt-1 flex items-center gap-2">
              {notif.sendBy?.image && (
                <img
                  src={notif.sendBy.image}
                  alt={notif.sendBy.fullName}
                  className="w-6 h-6 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-gray-900 font-medium">
                  {notif.sendBy?.fullName || "System"}
                </p>
                {notif.sendBy?.email && (
                  <p className="text-xs text-gray-500">{notif.sendBy.email}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Received At
            </label>
            <p className="mt-1 text-gray-700">
              {format(new Date(notif.createdAt), "PPP 'at' h:mm a")}
            </p>
          </div>

          {!notif.isRead && (
            <div className="pt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Unread
              </span>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={handleMarkRead}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            {!notif.isRead ? "Mark as Read & Close" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationRow({ notif, onRead, onOpenModal, onNavigate }) {
  const handleClick = () => {
    if (!notif?.isRead) onRead(notif?._id);

    const path = getRedirectPath(notif);
    if (path) {
      onNavigate(path);
    } else {
      onNavigate("/order/management");
      // onOpenModal(notif);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors ${notif.isRead ? "hover:bg-gray-50" : "bg-blue-50/40 hover:bg-blue-50/60"
        }`}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${notif.isRead
          ? "bg-gray-100 text-gray-600"
          : "bg-blue-100 text-blue-600"
          }`}
      >
        {notif.sendBy?.fullName?.[0]?.toUpperCase() || "N"}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">
            {notif.title || "Notification"}
          </p>
          <span className="text-xs text-gray-600 flex-shrink-0">
            {timeAgo(notif.createdAt)}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-0.5">{notif.message}</p>
        <p className="text-xs text-gray-600 mt-1 capitalize">
          From:{" "}
          {notif.sendBy?.fullName || notif.senderDetails?.fullName || "N/A"}
        </p>
      </div>

      {!notif.isRead && (
        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
      )}
    </div>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const userId = user?._id || user?.id;

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  // const [initialFetchDone, setInitialFetchDone] = useState(false);

  const socketRef = useRef(null);
  const dropdownRef = useRef(null);

  const loadingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await ApiClient.get(
        `notifications/list?isRead=false&page=1&limit=200000`,
      );
      if (res?.success) {
        const newData = res.data || [];
        setNotifications(newData);
        setTotalUnread(newData.filter((n) => !n.isRead).length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const markReadAPI = async (id) => {
    try {
      await ApiClient.put(`notifications/update`, { isRead: true, id });
      // setTotalUnread((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllReadAPI = async () => {
    try {
      await ApiClient.post(`notifications/mark-all-read`, { userId });
      setNotifications([]);
      setTotalUnread(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // useEffect(() => {
  //   if (!userId) return;

  //   if (!initialFetchDone) {
  //     fetchNotifications();
  //     setInitialFetchDone(true);
  //   }

  //   const socket = io(`${SOCKET_URL}?userId=${userId}`, {
  //     transports: ["websocket"],
  //     reconnection: true,
  //     reconnectionAttempts: 5,
  //   });

  //   socketRef.current = socket;

  //   socket.on("connect", () =>
  //     console.log("Socket connected for notifications"),
  //   );

  //   socket.on("notification", (newNotification) => {
  //     setNotifications((prev) => [newNotification, ...prev]);
  //     if (!newNotification.isRead) setTotalUnread((prev) => prev + 1);
  //   });

  //   socket.on("connect_error", (error) =>
  //     console.error("Socket connection error:", error),
  //   );

  //   return () => {
  //     if (socket) socket.disconnect();
  //   };
  // }, [userId, initialFetchDone, fetchNotifications]);

  useEffect(() => {
    if (!userId) return;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchNotifications();
    }

    const socket = io(`${SOCKET_URL}?userId=${userId}`, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected for notifications");
      fetchNotifications(); // reconnect hone par list resync ho jayegi
    });

    socket.on("notification", (newNotification) => {
      setNotifications((prev) => {
        if (prev.some((n) => n._id === newNotification._id)) return prev; // duplicate na bane
        return [newNotification, ...prev];
      });
      if (!newNotification.isRead) setTotalUnread((prev) => prev + 1);
    });

    socket.on("connect_error", (error) =>
      console.error("Socket connection error:", error),
    );

    return () => {
      socket.disconnect();
    };
  }, [userId, fetchNotifications]);

  const handleMarkRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n?._id === id ? { ...n, isRead: true } : n)),
    );
    setTotalUnread((prev) => Math.max(0, prev - 1));
    markReadAPI(id);
  }, []);

  const handleMarkAllRead = async () => {
    await markAllReadAPI();
  };

  const handleOpenModal = (notification) => {
    setSelectedNotification(notification);
    if (!notification.isRead) handleMarkRead(notification._id);
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
    setOpen(false);
  };

  // ─── Navigate and close dropdown ──────────────────────────────────────────
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleToggleDropdown = () => {
    setOpen((prev) => !prev);
    if (!open) fetchNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown}
        className=""
      >
        <IoIosNotificationsOutline size={22} />
        {totalUnread > 0 && (
          <span className="absolute top-[1px] right-[1px] inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[18px] h-[18px]">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        // <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 pb-5">
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-0 w-[95vw] max-w-sm md:w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 pb-5">
          <div className="px-4 py-3 border-b bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <IoIosNotificationsOutline
                    size={24}
                    className="text-gray-600"
                  />
                </div>
                <p className="text-gray-500 text-sm">No unread notifications</p>
                <p className="text-gray-600 text-xs mt-1">
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <NotificationRow
                    key={notif._id}
                    notif={notif}
                    onRead={handleMarkRead}
                    onOpenModal={handleOpenModal}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedNotification && (
        <NotificationModal
          notif={selectedNotification}
          onClose={handleCloseModal}
          onMarkRead={handleMarkRead}
        />
      )}
    </div>
  );
}
